import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { SessionProvider } from "./session/SessionContext";
import { PreferencesProvider } from "./preferences/PreferencesProvider";
import { SignInRedirect } from "./session/SignInRedirect";
import { ToastProvider } from "./components/toast/ToastProvider";
import { AppShell } from "./components/shell/AppShell";
import { HomeRoute } from "./routes/HomeRoute";
import { HelpPage } from "./routes/HelpPage";
import { NotFoundPage } from "./routes/NotFoundPage";
import { OrganisationPage } from "./routes/OrganisationPage";
import { MyGroupsPage } from "./routes/MyGroupsPage";
import { MyGroupDetailPage } from "./routes/MyGroupDetailPage";
import { PersonUsagePage } from "./routes/PersonUsagePage";
import { ConnectedApplicationsPage } from "./routes/ConnectedApplicationsPage";
import { PreferencesPage } from "./routes/PreferencesPage";
import { MyRequestsPage } from "./routes/MyRequestsPage";
import { MyToolsPage } from "./routes/MyToolsPage";
import { RolesPage } from "./routes/admin/RolesPage";
import { EntitlementAdminPage } from "./routes/admin/EntitlementAdminPage";
import { RequestsPage } from "./routes/admin/RequestsPage";
import { UserDetailPage } from "./routes/admin/UserHistoryPage";
import { SystemSettingsPage } from "./routes/admin/SystemSettingsPage";
import { ProvidersPage } from "./routes/admin/ProvidersPage";
import { EntitlementGroupsAdminPage } from "./routes/admin/EntitlementGroupsAdminPage";

// Redoc is large, and only the API reference uses it, so its page is loaded on first visit.
const ApiReferencePage = lazy(() => import("./routes/ApiReferencePage"));

/**
 * The application root: session wiring, the shell, and the application's routes.
 * BrowserRouter relies on the host serving index.html for any unmatched path (the SPA
 * fallback) so that a full page reload on a client route still boots the application. Phase 14
 * (`AIA-81`) added the organisation and unused-entitlement screens; phase 21 adds the eight
 * administration screens under `/admin/*`. Every route still enforces its own permission
 * internally (via `AdminGate` for the phase 21 screens), exactly like `MyConsumptionPage` already
 * does, so a route existing at all never implies access to what it shows.
 */
export function App(): React.JSX.Element {
  return (
    <SessionProvider>
      <PreferencesProvider>
      <ToastProvider>
        <BrowserRouter>
          <AppShell>
            <SignInRedirect>
              <Routes>
                <Route path="/" element={<HomeRoute />} />
                <Route path="/help" element={<HelpPage />} />
                <Route
                  path="/rest-api/*"
                  element={
                    <Suspense fallback={null}>
                      <ApiReferencePage />
                    </Suspense>
                  }
                />
                <Route path="/organization" element={<OrganisationPage />} />
                {/* The Organisation page's old address still arrives there. */}
                <Route path="/organisation" element={<Navigate to="/organization" replace />} />
                <Route path="/my-groups" element={<MyGroupsPage />} />
                <Route path="/preferences" element={<PreferencesPage />} />
                <Route path="/my-groups/:groupId" element={<MyGroupDetailPage />} />
                <Route path="/my-groups/:groupId/people/:personId" element={<PersonUsagePage />} />
                <Route path="/organization/people/:personId" element={<PersonUsagePage />} />
                <Route path="/organisation/people/:personId" element={<PersonUsagePage />} />
                <Route path="/people/:personId" element={<PersonUsagePage backTo="history" />} />
                <Route path="/my-requests" element={<MyRequestsPage />} />
                <Route path="/my-tools" element={<MyToolsPage />} />
                <Route
                  path="/connected-applications"
                  element={<ConnectedApplicationsPage />}
                />
                {/* Unused entitlement and Account matching are now System's Accounts tab. */}
                <Route path="/admin/unused-entitlement" element={<Navigate to="/admin/system/accounts" replace />} />
                <Route path="/admin/identity-queue" element={<Navigate to="/admin/system/accounts" replace />} />
                <Route path="/admin/roles" element={<RolesPage />} />
                <Route path="/admin/roles/:tab" element={<RolesPage />} />
                <Route path="/admin/users" element={<EntitlementAdminPage />} />
                <Route
                  path="/admin/users/:personId"
                  element={<UserDetailPage />}
                />
                <Route path="/admin/requests" element={<RequestsPage />} />
                <Route path="/admin/providers" element={<ProvidersPage />} />
                <Route
                  path="/admin/providers/:tab"
                  element={<ProvidersPage />}
                />
                <Route path="/admin/provider-access" element={<Navigate to="/admin/providers/provider-access" replace />} />
                <Route path="/admin/bedrock-models" element={<Navigate to="/admin/providers/bedrock-models" replace />} />
                <Route path="/admin/system/write-safety" element={<Navigate to="/admin/providers/write-safety" replace />} />
                <Route path="/admin/system" element={<SystemSettingsPage />} />
                <Route
                  path="/admin/system/:tab"
                  element={<SystemSettingsPage />}
                />
                <Route
                  path="/admin/groups"
                  element={<EntitlementGroupsAdminPage />}
                />
                <Route path="/admin/configuration" element={<Navigate to="/admin/system/configuration" replace />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </SignInRedirect>
          </AppShell>
        </BrowserRouter>
      </ToastProvider>
      </PreferencesProvider>
    </SessionProvider>
  );
}
